// Buyer-facing, module-specific outcomes and users for every public /modules/:key route.
//
// This registry deliberately does not fall back by marketing family. A fee accountant is not a
// payroll operator, a parent is not a staff-shift manager, and a group-level workflow is not a
// truthful substitute for a feature's own outcome. Copy is grounded in featureContent.js and the
// product routes represented by AppLayout.js; prerequisites preserve the same plan, provider,
// device, release and source-data boundaries used by the public claims registry.

const profile = (outcome, roles, prerequisites = []) => Object.freeze({
  outcome,
  roles: Object.freeze(roles),
  ...(prerequisites.length ? { prerequisites: Object.freeze(prerequisites) } : {}),
});

const MODULE_PROFILE_CONTENT = Object.freeze({
  // Academics
  "timetable-management": profile(
    "A publish-ready weekly timetable with clashes, workload caps and unresolved placements visible before save.",
    ["Timetable administrator", "Academic coordinator", "Principal", "Teacher"],
    ["Classes, sections, subjects, teachers, working days and period configuration"],
  ),
  "curriculum-planning": profile(
    "A governed chapter-and-topic curriculum that lesson plans and coverage reviews can reference consistently.",
    ["Academic coordinator", "Head of department", "Teacher", "Principal"],
    ["Configured classes and subjects"],
  ),
  "lesson-plan-management": profile(
    "Reviewable lesson plans with objectives, resources, curriculum links, completion state and HoD decisions in one record.",
    ["Teacher", "Head of department", "Academic coordinator", "Principal"],
    ["Teaching assignments and configured classes/subjects", "A plan that includes Aira, plus adopted syllabus text for AI drafting"],
  ),
  "homework-and-assignments": profile(
    "A section-scoped assignment register showing what was assigned, recorded as received, graded and still outstanding.",
    ["Teacher", "Academic coordinator", "Student", "Parent or guardian"],
    ["Student roster and teaching assignment", "Configured messaging provider and consent for guardian reminders"],
  ),
  "digital-gradebook": profile(
    "A board-configured marks register with clamped scores, explicit absences and controlled locks after publication.",
    ["Subject teacher", "Exam coordinator", "Academic coordinator", "Principal"],
    ["Board configuration, exam papers and teacher section scope"],
  ),
  "exam-scheduling": profile(
    "A class-and-subject exam schedule with dates, maximum marks and conflicts reviewed before the datesheet is used.",
    ["Exam coordinator", "Academic coordinator", "Principal", "Subject teacher"],
    ["Classes, sections, subjects, teachers and exam-term configuration"],
  ),
  "result-publishing": profile(
    "Issued board-aware report cards whose grading, template, attendance and signatory context remain reproducible on re-download.",
    ["Exam coordinator", "Class teacher", "Academic coordinator", "Principal", "Student", "Parent or guardian"],
    ["Completed marks, board configuration and an approved report-card template"],
  ),
  "subject-management": profile(
    "One governed subject catalogue used consistently by teaching assignments, timetables, homework and examinations.",
    ["Academic administrator", "Academic coordinator", "Timetable administrator", "Exam coordinator"],
  ),
  "teacher-mapping": profile(
    "An authoritative class-section-subject assignment map that also defines each teacher's record scope.",
    ["Academic coordinator", "Timetable administrator", "Principal", "Teacher"],
    ["Active staff records, classes, sections and subjects"],
  ),
  "syllabus-tracking": profile(
    "A live, filterable view of planned, in-progress, completed and approved chapter coverage.",
    ["Teacher", "Head of department", "Academic coordinator", "Principal"],
    ["Curriculum chapters and linked lesson plans"],
  ),
  "academic-calendar": profile(
    "One authorised calendar for terms, examinations, events, working days and school holidays.",
    ["School administrator", "Academic coordinator", "Principal", "Teacher"],
    ["Academic year and board configuration"],
  ),
  "online-classes": profile(
    "An organised schedule of external Zoom, Meet or Teams sessions with a reliable join destination.",
    ["Teacher", "Academic coordinator", "Student"],
    ["A plan that includes Digital Classroom (LMS)", "A valid school- or teacher-supplied external meeting URL"],
  ),
  "study-material": profile(
    "Ordered course resources—notes, videos, links and files—published against the correct class or section.",
    ["Teacher", "Academic coordinator", "Student"],
    ["A plan that includes Digital Classroom (LMS), plus configured courses"],
  ),
  "student-performance": profile(
    "Explainable attendance and academic signals that help authorised teams identify students needing review or intervention.",
    ["Class teacher", "Academic coordinator", "Principal", "Authorised student-support staff"],
    ["Sufficient attendance and marks records", "A plan that includes Insights and Aira for optional AI explanations"],
  ),
  "class-management": profile(
    "A governed class structure with capacity, class-teacher and academic-ladder rules reused across school workflows.",
    ["School administrator", "Academic coordinator", "Admissions officer", "Principal"],
  ),
  "section-management": profile(
    "Balanced class sections with capacity checks, unique roll numbers and a clear unit for teacher access control.",
    ["School administrator", "Academic coordinator", "Admissions officer", "Principal"],
    ["Configured classes"],
  ),
  "certificate-generation": profile(
    "Serialized, branded and QR-verifiable certificates with issue, re-download and revocation evidence.",
    ["Registrar", "School administrator", "Principal", "Authorised certificate issuer", "Student or employee"],
    ["A plan that includes Certificates", "Configured certificate type/template and authorised source record"],
  ),
  "promotion-management": profile(
    "A checksum-gated year-end promotion result with explicit hold-backs, graduates and an all-or-nothing execution record.",
    ["School administrator", "Academic coordinator", "Principal", "Class teacher"],
    ["New academic year, active enrolments and a reviewed dry-run checksum"],
  ),

  // Administration and operations
  "student-admission": profile(
    "A validated student record with a server-issued admission number, guardian links and controlled class-section allocation.",
    ["Admissions officer", "Front-office executive", "School administrator", "Principal"],
    ["Configured classes/sections and admission-number settings"],
  ),
  "enquiry-management": profile(
    "An owned admissions pipeline from first enquiry through follow-up, application decision and controlled student conversion.",
    ["Admissions counsellor", "Front-office executive", "Admissions head", "Principal"],
    ["Admissions stages and assignment rules", "A plan that includes Aira for optional follow-up drafting"],
  ),
  "staff-management": profile(
    "A controlled employee directory with statutory identifiers, masked finance details and a traceable active/deactivated lifecycle.",
    ["HR administrator", "School administrator", "Principal", "Employee"],
    ["HR & Payroll enabled for your school (still rolling out school by school)"],
  ),
  "role-and-permission": profile(
    "An auditable module-and-action access model with record scope and separation-of-duties boundaries enforced on requests.",
    ["Tenant administrator", "Security administrator", "School owner", "Principal"],
    ["Defined user accounts and role responsibilities"],
  ),
  "id-card-management": profile(
    "Print-ready student identity cards with live-record QR verification and controlled class or school-wide batches.",
    ["School administrator", "Registrar", "Front-office executive", "Class coordinator"],
    ["Student identity/photo records and school branding"],
  ),
  "document-management": profile(
    "A permission-scoped document collection register showing requested, submitted, verified, rejected, missing and expiring items.",
    ["Registrar", "School administrator", "HR administrator", "Student or employee"],
    ["Configured required-document types and object storage"],
  ),
  "transport-management": profile(
    "A connected route, stop, fleet, crew and rider operation with capacity, compliance-expiry and trip evidence visible together.",
    ["Transport manager", "School administrator", "Fleet coordinator", "Accountant", "Parent or guardian"],
    ["Routes, stops, vehicles, crew and student allocations", "Compatible location input for live bus tracking"],
  ),
  "task-management": profile(
    "Assigned school work with recurrence, SLA, checklist, proof, review, escalation and completion evidence in one place.",
    ["School owner", "Principal", "Operations manager", "Task assignee", "Task reviewer"],
    ["Users or roles available for assignment", "Aira credits and configured vision provider for optional AI proof checks"],
  ),
  "hostel-management": profile(
    "A current resident-and-bed register with room occupancy, allocation history and controlled gate-pass movements.",
    ["Hostel warden", "School administrator", "Accountant", "Parent or guardian"],
    ["A plan that includes Hostel, plus configured rooms/beds"],
  ),
  "inventory-management": profile(
    "An append-only stock ledger with controlled receipts, issues, physical counts and valuation evidence.",
    ["Storekeeper", "Purchase officer", "Inventory approver", "Accountant", "School administrator"],
    ["A plan that includes Inventory, plus configured stores/items"],
  ),
  "library-management": profile(
    "A live catalogue and circulation record covering copies, loans, returns, reservations and overdue amounts.",
    ["Librarian", "School administrator", "Student", "Employee"],
    ["A plan that includes Library, plus catalogued books/copies"],
  ),
  "visitor-management": profile(
    "A current on-premises visitor register with pre-registration, identity-minimised check-in and completed check-out duration.",
    ["Receptionist", "Security officer", "School administrator", "Employee host"],
    ["A plan that includes Front Office"],
  ),
  "front-office": profile(
    "A reception command view for visitors, gate passes, walk-in enquiries and general complaints needing follow-up.",
    ["Receptionist", "Front-office manager", "Admissions officer", "School administrator", "Principal"],
    ["A plan that includes Front Office"],
  ),
  "asset-management": profile(
    "A fixed-asset register with location, status, depreciation, book value and disposal evidence.",
    ["Asset custodian", "Accountant", "Purchase officer", "School administrator"],
    ["A plan that includes Inventory, plus configured accounting periods"],
  ),
  "branch-management": profile(
    "A group-level view of member schools with governed cross-campus comparison, transfers, branding and consolidated billing evidence.",
    ["Trust owner", "Group administrator", "Campus head", "Group finance manager"],
    ["A plan that includes Group & Chain, plus a configured legal-entity membership model"],
  ),
  "user-management": profile(
    "Provisioned staff accounts with one-time credential handover, forced first-password change, 2FA and immutable login history.",
    ["Tenant administrator", "IT administrator", "Security administrator", "School owner"],
    ["Verified staff identity and assigned role"],
  ),
  "institute-settings": profile(
    "One controlled source for school identity, board, academic year, branding, GST and onboarding go-live settings.",
    ["Tenant administrator", "School owner", "Principal", "Authorised finance administrator"],
  ),
  "approval-workflow": profile(
    "Recorded second-approver decisions at supported money, leave, stock and registration control points.",
    ["Requestor", "Authorised approver", "School administrator", "Auditor"],
    ["The underlying module and its configured permissions; this is not an unrestricted approval builder"],
  ),
  "custom-forms": profile(
    "Published forms, governed custom student fields and tenant-defined objects that extend supported school records without code changes.",
    ["Tenant administrator", "Process owner", "Form designer", "Authorised respondent", "Data reviewer"],
    ["Defined field purpose, audience, visibility and retention rules"],
  ),

  // Fees and finance
  "fee-structure": profile(
    "A reusable class-wise fee plan with exact-paise heads, tax metadata, frequency and controlled demand generation.",
    ["Fee administrator", "Accountant", "Finance approver", "Principal"],
    ["Classes, billing periods and fee-head/tax decisions"],
  ),
  "fee-collection": profile(
    "One invoice-linked collection ledger for authorised counter evidence and provider-confirmed online payments.",
    ["Fee-desk executive", "Accountant", "Finance approver", "Parent or guardian"],
    ["Issued invoices", "Configured school-owned provider for online collection"],
  ),
  "online-payment": profile(
    "An invoice-bound no-login payment path with server-derived amounts and a provider-confirmed result.",
    ["Parent or guardian", "Fee-desk executive", "Accountant"],
    ["School-owned Razorpay/Cashfree configuration or an authorised direct-UPI verification flow"],
  ),
  "pending-fee-tracking": profile(
    "A current, filterable view of due and overdue exposure by invoice and student, including age and oldest due date.",
    ["Fee-desk executive", "Accountant", "Principal", "Finance approver"],
    ["Issued invoices and due dates"],
  ),
  "discount-and-scholarship": profile(
    "Approved fee relief recorded as bounded, auditable invoice adjustments rather than silent balance edits.",
    ["Fee-relief requestor", "Scholarship committee", "Authorised finance approver", "Accountant", "Principal"],
    ["Configured concession/scholarship schemes and second-approver permissions"],
  ),
  "fine-management": profile(
    "A previewed and idempotent late-fee posting from a bounded rule with grace period and cap.",
    ["Fee administrator", "Accountant", "Finance approver"],
    ["Overdue invoices, open accounting period and a configured late-fee rule"],
  ),
  "receipt-generation": profile(
    "Sequential branded receipts tied to recorded payment evidence, with controlled reprints and tax breakup where applicable.",
    ["Fee-desk executive", "Accountant", "Finance auditor", "Parent or guardian"],
    ["Recorded payment evidence and receipt-template configuration"],
  ),
  "installment-setup": profile(
    "A dated payment plan whose installment status is always derived from the invoice's real cumulative payments.",
    ["Fee-desk executive", "Accountant", "Finance approver", "Parent or guardian"],
    ["An open invoice with an outstanding balance"],
  ),
  "expense-management": profile(
    "Approved, numbered and period-controlled expense vouchers that post once to the school's books.",
    ["Expense requestor", "Accountant", "Authorised finance approver", "Principal"],
    ["A plan that includes Accounting, plus a chart of accounts and an open period", "A plan that includes Aira for optional category suggestions"],
  ),
  payroll: profile(
    "A controlled monthly salary run with statutory calculations, approved disbursal evidence, payslips and ledger-ready results.",
    ["Payroll officer", "HR administrator", "Finance approver", "Employee", "Statutory reviewer"],
    ["HR & Payroll enabled for your school (still rolling out school by school), plus completed employee/payroll inputs"],
  ),
  "accounting-reports": profile(
    "Reconciled management statements and named registers derived from fee, payment, expense and journal evidence.",
    ["Accountant", "Bursar", "School owner", "Principal", "Auditor or chartered accountant"],
    ["A plan that includes Accounting, plus a chart of accounts and posted source transactions"],
  ),
  "payment-gateway": profile(
    "A verified school-owned Razorpay or Cashfree connection with protected credentials, callback evidence and visible settlement exceptions.",
    ["Payment administrator", "Accountant", "Tenant administrator", "Finance approver"],
    ["The school's own provider account, credentials and webhook configuration"],
  ),
  "refund-management": profile(
    "A two-person refund trail whose fee ledger changes only after the source-payment refund receives its supported verdict.",
    ["Refund requestor", "Accountant", "Authorised finance approver", "Parent or guardian"],
    ["A refundable source payment and an open accounting period"],
  ),
  "fee-reminder": profile(
    "A supervised overdue-recovery queue with bounded scoring, staged follow-up, paid-state suppression and a final human call task.",
    ["Fee-desk executive", "Accountant", "Recovery supervisor", "Parent or guardian"],
    ["A plan that includes Automation, plus overdue invoices, approved templates, configured channels and consent controls"],
  ),
  "category-wise-fees": profile(
    "Consistent fee pricing by supported class or student category without creating an untraceable parallel amount.",
    ["Fee administrator", "Accountant", "Admissions officer", "Finance approver"],
    ["Configured fee structures and supported category/concession schemes"],
  ),
  "transport-fees": profile(
    "A previewed monthly rider-billing result with proration, waitlist/suspension exclusions and its own accounting classification.",
    ["Transport manager", "Accountant", "Fee-desk executive", "Finance approver"],
    ["Active transport allocations, route pricing and fee/tax configuration"],
  ),
  "hostel-fees": profile(
    "Idempotent monthly resident charges on the same invoice, receipt and reconciliation path as other school fees.",
    ["Hostel warden", "Accountant", "Fee-desk executive", "Parent or guardian"],
    ["A plan that includes Hostel, plus active resident allocations and configured hostel fee heads"],
  ),
  "financial-summary": profile(
    "A current finance command view of billed, collected, pending, overdue and collection-rate figures with supported breakdowns.",
    ["Accountant", "Bursar", "Principal", "School owner", "Finance approver"],
    ["Issued invoices and recorded payment evidence", "A plan that includes Aira for optional narrative/Q&A"],
  ),

  // Attendance and workforce time
  "student-attendance": profile(
    "An auditable daily or period register with fast marking, explicit late/leave states and controlled corrections.",
    ["Class teacher", "Subject teacher", "Attendance coordinator", "Principal", "Parent or guardian"],
    ["Student roster and teacher section scope"],
  ),
  "staff-attendance": profile(
    "Workforce presence evidence — still rolling out school by school — that authorised teams can compare with leave, schedules and exceptions.",
    ["HR administrator", "Workforce manager", "Principal", "Employee"],
    ["HR & Payroll enabled for your school, plus governed workforce-time configuration and employee identities"],
  ),
  "biometric-attendance": profile(
    "A reviewable device-punch stream where matched student IN events may mark present without overwriting a teacher's register.",
    ["Attendance administrator", "Device administrator", "Class teacher", "School administrator"],
    ["A plan that includes Biometric Devices, plus compatible school-supplied device middleware"],
  ),
  "qr-attendance": profile(
    "A compatible QR scan-to-punch path with tenant-scoped matching, idempotency and an unmatched-event review queue.",
    ["Attendance administrator", "Device administrator", "Class teacher"],
    ["Compatible QR scanning middleware and an authorised device token"],
  ),
  "rfid-attendance": profile(
    "A compatible RFID tap-to-punch path with tenant-scoped matching, token rotation and unmatched-event visibility.",
    ["Attendance administrator", "Device administrator", "Class teacher"],
    ["Compatible RFID reader middleware and an authorised device token"],
  ),
  "leave-management": profile(
    "A quota-aware staff leave record with request, approval, balance, notification and calendar evidence.",
    ["Employee", "Line manager", "HR administrator", "Principal"],
    ["HR & Payroll enabled for your school (still rolling out school by school), plus leave types and role hierarchy"],
  ),
  "holiday-management": profile(
    "One authorised holiday and working-day calendar reused by supported academic and operational schedules.",
    ["School administrator", "Academic coordinator", "Principal", "Transport manager"],
    ["Academic year and calendar configuration"],
  ),
  "late-entry-tracking": profile(
    "A distinct late status with arrival evidence, breakdown reporting and a retained correction history.",
    ["Class teacher", "Attendance coordinator", "Principal", "Parent or guardian"],
    ["Saved attendance registers or timestamped compatible device punches"],
  ),
  "daily-attendance-report": profile(
    "A selected day's attendance rate and class-section breakdown derived from saved registers.",
    ["Attendance coordinator", "Class teacher", "Principal", "Academic coordinator"],
    ["Saved attendance for the selected date"],
  ),
  "monthly-attendance-report": profile(
    "A student-by-day month matrix with per-student rates and an optional single-period view.",
    ["Attendance coordinator", "Class teacher", "Principal", "Academic coordinator"],
    ["Saved attendance registers for the selected month"],
  ),
  "parent-notification": profile(
    "A consent-aware guardian alert attempt tied to a saved absence or supported gate event, with provider-returned status.",
    ["Attendance coordinator", "Class teacher", "Communication administrator", "Parent or guardian"],
    ["Configured messaging provider, consent/opt-out state and enabled attendance alert policy"],
  ),
  "period-attendance": profile(
    "A subject-period register that remains reviewable beside the whole-day register and rolls up by a documented rule.",
    ["Subject teacher", "Class teacher", "Attendance coordinator", "Student"],
    ["Period configuration, timetable/teacher scope and student roster"],
  ),
  "attendance-analytics": profile(
    "Current attendance trends, class comparisons and threshold watchlists computed from saved registers.",
    ["Principal", "Attendance coordinator", "Academic coordinator", "Class teacher"],
    ["A sufficient history of marked attendance and board-threshold configuration"],
  ),
  "auto-absence-alert": profile(
    "Damped same-day and threshold attendance alerts that suppress duplicate sends and respect the latest register state.",
    ["Attendance coordinator", "Communication administrator", "Class teacher", "Parent or guardian"],
    ["Configured provider, approved template, consent/opt-outs and an enabled alert rule"],
  ),
  "staff-shift-tracking": profile(
    "Governed workforce schedules, shift assignments and coverage states that employees and authorised managers can review.",
    ["Workforce scheduler", "HR administrator", "Line manager", "Employee", "Principal"],
    ["HR & Payroll enabled for your school (still rolling out school by school), plus organisation structure, employees and shift definitions"],
  ),

  // Reports and analytics
  "student-reports": profile(
    "Governed student, enrolment, RTE and CWSN tables with filters and supported export evidence.",
    ["Principal", "Registrar", "School administrator", "Compliance officer"],
    ["A plan that includes Reports, plus current student/enrolment records"],
  ),
  "fee-reports": profile(
    "Exact-paise collection, outstanding, ageing and defaulter reports that reconcile to supported finance sources.",
    ["Accountant", "Bursar", "Principal", "School owner"],
    ["A plan that includes Reports, plus issued invoice/payment records"],
  ),
  "attendance-reports": profile(
    "Class-section attendance summaries, trends and board-threshold views derived from saved registers.",
    ["Principal", "Attendance coordinator", "Academic coordinator", "Class teacher"],
    ["A plan that includes Reports, plus attendance history and board-threshold configuration"],
  ),
  "academic-reports": profile(
    "Board-aware subject performance, CCE roll-up and class-result reports from published academic records.",
    ["Principal", "Exam coordinator", "Academic coordinator", "Head of department"],
    ["A plan that includes Reports, plus board configuration and recorded marks"],
  ),
  "exam-reports": profile(
    "Per-exam statistics, rankings and supported board-portal exports with optional aggregate-only explanation.",
    ["Exam coordinator", "Academic coordinator", "Principal", "Subject teacher"],
    ["A plan that includes Reports, plus entered exam marks", "A plan that includes Aira for optional AI analysis"],
  ),
  "admission-reports": profile(
    "Stage, source and conversion reports showing where admissions enquiries progress or stall.",
    ["Admissions head", "Admissions counsellor", "Principal", "School owner"],
    ["A plan that includes Reports, plus admissions pipeline history"],
  ),
  "staff-reports": profile(
    "Headcount, leave, workload and payroll views — still rolling out school by school — scoped to the authorised workforce population.",
    ["HR administrator", "Workforce analyst", "Payroll officer", "Principal", "School owner"],
    ["HR & Payroll enabled for your school, plus the applicable Reports/Workforce Analytics features on your plan"],
  ),
  "transport-reports": profile(
    "Route utilisation, expected-versus-boarded, manifest and fleet-expiry evidence for daily transport review.",
    ["Transport manager", "Fleet coordinator", "Principal", "School administrator"],
    ["A plan that includes Reports, plus configured transport/trip records"],
  ),
  "library-reports": profile(
    "Current circulation, overdue, fine and reservation indicators tied back to books, copies and loans.",
    ["Librarian", "School administrator", "Principal"],
    ["A plan that includes Library, plus circulation history"],
  ),
  "custom-reports": profile(
    "A constrained grouped roll-up over allow-listed student or invoice dimensions and measures.",
    ["Principal", "School owner", "Registrar", "Accountant", "Authorised report analyst"],
    ["A plan that includes Reports, plus access to the selected source"],
  ),
  "dashboard-analytics": profile(
    "A role-relevant current view of students, fees, attendance, tasks and items needing attention.",
    ["School owner", "Principal", "Module manager", "Teacher", "Accountant"],
    ["Permissions for each source shown", "A plan that includes Aira for optional briefing narration"],
  ),
  "performance-insights": profile(
    "An explainable student-risk ranking with driver contributions and a controlled path into intervention tasks.",
    ["Principal", "Academic coordinator", "Class teacher", "Authorised student-support staff"],
    ["A plan that includes Insights, plus sufficient attendance, marks and fee signals"],
  ),
  "export-pdf": profile(
    "Supported branded operational documents—report cards, receipts, payslips, ID cards and certificates—with their own issue rules.",
    ["Authorised document issuer", "Registrar", "Accountant", "Payroll officer", "Student or employee"],
    ["The source module, its output permission and a configured document template"],
  ),
  "export-excel": profile(
    "Filter-aware CSV or XLSX exports with numeric money fields, permission checks and a recorded data-egress event.",
    ["Authorised report analyst", "Principal", "Registrar", "Accountant", "Auditor"],
    ["Reports/source-module export permission"],
  ),
  "real-time-charts": profile(
    "Request-time operational charts with defined comparison periods and accessible underlying values where supported.",
    ["School owner", "Principal", "Module manager", "Authorised analyst"],
    ["Permission to each source dashboard; no event-stream guarantee"],
  ),
  "branch-wise-reports": profile(
    "A governed cross-campus comparison of member-school KPIs with group-wide totals and consolidated billing evidence.",
    ["Trust owner", "Group administrator", "Campus head", "Group finance manager"],
    ["A plan that includes Group & Chain, plus legal-entity membership"],
  ),
  "daily-summary": profile(
    "A leader's current morning briefing of attendance, fee exposure, student risk and open tasks needing review.",
    ["Principal", "School owner", "Campus head"],
    ["Permission to the contributing modules", "A plan that includes Aira for optional prose narration"],
  ),
  "mis-reports": profile(
    "One canonical catalogue of parameterised school MIS reports with a uniform table, KPI and export shape.",
    ["Principal", "School owner", "School administrator", "Authorised report analyst"],
    ["A plan that includes Reports, plus permissions for the selected report source", "A plan that includes Aira for natural-language routing"],
  ),

  // Communication and role experiences
  "sms-alerts": profile(
    "DLT-aligned transactional SMS attempts with per-type templates, retry handling and provider-returned delivery evidence.",
    ["Communication administrator", "School administrator", "Template approver", "Message recipient"],
    ["MSG91 credentials, registered sender ID, DLT template IDs, consent and applicable policy"],
  ),
  "whatsapp-alerts": profile(
    "Approved-template WhatsApp communication with school-owned provider configuration, opt-outs and ordered delivery/read states.",
    ["Communication administrator", "School administrator", "Template approver", "Parent or guardian"],
    ["School WABA/BSP credentials, Meta-approved templates and consent controls"],
  ),
  "email-notifications": profile(
    "Branded transactional or permitted bulk email with protected provider configuration, unsubscribe handling and send logs.",
    ["Communication administrator", "School administrator", "Teacher", "Message recipient"],
    ["Configured SendGrid/SMTP delivery and recipient eligibility"],
  ),
  "push-notifications": profile(
    "Best-effort native notifications that can deep-link an eligible user into a supported app screen without blocking the source workflow.",
    ["Parent", "Student", "Teacher", "School administrator"],
    ["Early-access native app, registered device and configured push delivery"],
  ),
  "parent-app": profile(
    "An own-child-scoped mobile view of supported fees, receipts, attendance, results, homework, transport, PTM and certificates.",
    ["Parent or guardian", "School administrator"],
    ["Early-access native app distribution and a claimed/linked parent account"],
  ),
  "student-app": profile(
    "A Class-5-and-up student mobile view of supported timetable, homework, attendance, results, class chat and achievements.",
    ["Student", "Teacher", "School administrator"],
    ["Early-access native app distribution and an eligible active student account"],
  ),
  "teacher-app": profile(
    "A mobile staff workspace for supported attendance, tasks, proof capture, schedules and best-effort notifications.",
    ["Teacher", "School administrator", "Academic coordinator", "Task reviewer"],
    ["Early-access native app distribution", "HR & Payroll and Aira enabled for the school, which unlock the conditional self-service and assistant views"],
  ),
  "notice-board": profile(
    "Targeted digital notices with category, pin, expiry and optional configured-channel publication evidence.",
    ["Communication administrator", "School administrator", "Teacher", "Parent or student recipient"],
    ["Audience records and configured channels for off-platform delivery"],
  ),
  announcements: profile(
    "A scheduled or immediate broadcast with an explicit audience preview, deduplication and per-channel delivery evidence.",
    ["Communication administrator", "School administrator", "Principal", "Teacher"],
    ["Eligible audience records and at least one configured delivery channel"],
  ),
  "event-updates": profile(
    "Scheduled event communication with a defined audience and provider-reported attempted, delivered, skipped and failed states.",
    ["Event coordinator", "Communication administrator", "PTM coordinator", "Parent or guardian"],
    ["An event/PTM record, eligible audience and configured channel"],
  ),
  "homework-updates": profile(
    "Read-only assignment visibility for families plus guarded non-submitter reminder attempts from the teacher's roster.",
    ["Teacher", "Student", "Parent or guardian", "Academic coordinator"],
    ["Published homework and a configured WhatsApp provider for reminders"],
  ),
  "automated-fee-reminders": profile(
    "Scheduled pre-due and authorised manual/bulk payment nudges with cooldowns, pay links and paid-state suppression.",
    ["Fee-desk executive", "Accountant", "Communication administrator", "Parent or guardian"],
    ["Issued invoices, configured channel/templates, consent controls and a provider-backed pay link where used"],
  ),
  "attendance-alerts": profile(
    "Same-day, threshold, optional gate and monthly-digest attendance notifications with consent and provider status retained.",
    ["Attendance coordinator", "Communication administrator", "Class teacher", "Parent or guardian"],
    ["Saved attendance/device evidence, enabled alert policy, configured provider and consent controls"],
  ),
  "complaint-management": profile(
    "Traceable general complaints and separately permissioned private concerns, each with its own visibility and resolution path.",
    ["Front-office executive", "Grievance officer", "Principal", "School administrator", "Student or employee submitter"],
    ["Configured complaint categories and explicit private-grievance permissions"],
  ),
  "chat-system": profile(
    "Section- and subject-scoped class rooms with teacher moderation, no private DMs and an administrative audit trail.",
    ["Student", "Subject teacher", "Class teacher", "Chat moderator", "School administrator"],
    ["Timetable/teaching assignments and active section membership"],
  ),
  "circular-management": profile(
    "An approved circular record with category, archive state, configured-channel dispatch and delivery statistics.",
    ["School administrator", "Principal", "Communication administrator", "Parent or employee recipient"],
    ["Configured templates/channels", "A plan that includes Aira for optional draft generation"],
  ),
  "birthday-wishes": profile(
    "A role-scoped birthday surface and idempotent greeting record that does not expose age, birth year or full date of birth.",
    ["School administrator", "Teacher", "Student", "Employee"],
    ["Authorised date-of-birth records and enabled display rules"],
  ),
  "emergency-alerts": profile(
    "An urgent, audience-scoped broadcast attempt across selected configured channels with each delivery state retained.",
    ["Principal", "School administrator", "Communication administrator", "Transport manager", "Staff or parent recipient"],
    ["Emergency audience, configured channels/templates and authorised sender"],
  ),

  // Aira, migration and automation
  "aira-assistant": profile(
    "Permission-scoped answers over supported school records plus reviewable actions that execute only after the relevant confirmation.",
    ["School owner", "Principal", "School administrator", "Accountant", "Teacher"],
    ["A plan that includes the Aira AI assistant, plus credits and permissions for each requested source", "Configured voice provider for voice input/read-aloud"],
  ),
  "parent-aira": profile(
    "Own-child-only answers for a guardian across supported fees, attendance, published marks and transport data.",
    ["Parent or guardian", "School administrator"],
    ["A plan that includes Aira, plus linked parent-child identity and a school-enabled channel/credit budget"],
  ),
  "voice-aira": profile(
    "A reviewable voice record: spoken questions and cleaned dictation where enabled, plus consented meeting drafts with summaries, decisions and assigned follow-ups.",
    ["Teacher", "Admissions counsellor", "Principal or vice-principal", "Authorised Aira user", "Parent or guardian"],
    [
      "A plan that includes Aira, plus credits, microphone permission and a configured Sarvam voice provider for spoken input and read-aloud",
      "Meeting Notes must also be included on the school's plan; pasted transcripts remain available when live voice is not configured",
    ],
  ),
  "migration-copilot": profile(
    "A reviewed student CSV/XLSX import with suggested mapping, row-level validation, preview, controlled commit and rollback evidence.",
    ["Migration administrator", "Admissions administrator", "Data migration operator", "School administrator"],
    ["A plan that includes the Migration Copilot, plus a supported student CSV/XLSX file; staff, fee-demand and accounting import are out of scope"],
  ),
  "compliance-ai": profile(
    "Rule-based UDISE+, APAAR and DPDP readiness findings with an optional plain-language explanation of the cited rule and fix.",
    ["Compliance officer", "Principal", "SIS administrator", "School administrator"],
    ["Eligible student/consent records", "Aira credits only for optional AI explanation"],
  ),
  "report-card-ai": profile(
    "Board-aware class-teacher remark drafts derived from permitted marks and attendance, held for teacher review before publication.",
    ["Class teacher", "Exam coordinator", "Academic coordinator"],
    ["A plan that includes Aira (with credits), plus board configuration and permitted marks/attendance records"],
  ),
  "website-builder-ai": profile(
    "A reviewable multi-page school-site draft with supported edits, SEO copy and enabled enquiry blocks linked to admissions.",
    ["School owner", "Principal", "Website administrator", "Communication or admissions lead"],
    ["Aira and the website builder enabled for the school, plus approved public content and a human publishing review"],
  ),
  "talk-to-your-data": profile(
    "Plain-language answers routed to vetted reports, with platform-computed figures and only a scrubbed aggregate digest sent for narration.",
    ["School owner", "Principal", "Academic coordinator", "Accountant", "Authorised report user"],
    ["A plan that includes Aira and Reports, plus permission for the selected canonical report"],
  ),
  automations: profile(
    "A disabled-by-default library of 40 starter rules plus a simulated, versioned rule builder and per-step run evidence.",
    ["Tenant administrator", "Process owner", "Module manager", "Automation reviewer", "Auditor"],
    ["A plan that includes Automation, plus configured triggers/actions and an authorised decision to enable each rule"],
  ),
});

// Explicit workflow neighbours only. These links represent a source-record hand-off, a configured
// dependency, an output consumed by the neighbour, or two views over the same governed record. They
// intentionally replace same-family rotation: proximity in the marketing catalogue is not evidence
// that two capabilities integrate.
export const MODULE_RELATIONSHIPS = Object.freeze({
  "timetable-management": Object.freeze(["subject-management", "teacher-mapping", "class-management", "academic-calendar"]),
  "curriculum-planning": Object.freeze(["lesson-plan-management", "syllabus-tracking", "subject-management", "study-material"]),
  "lesson-plan-management": Object.freeze(["curriculum-planning", "syllabus-tracking", "teacher-mapping", "study-material"]),
  "homework-and-assignments": Object.freeze(["teacher-mapping", "section-management", "homework-updates", "student-app"]),
  "digital-gradebook": Object.freeze(["exam-scheduling", "result-publishing", "academic-reports", "exam-reports"]),
  "exam-scheduling": Object.freeze(["digital-gradebook", "result-publishing", "academic-calendar", "exam-reports"]),
  "result-publishing": Object.freeze(["digital-gradebook", "exam-reports", "report-card-ai", "export-pdf"]),
  "subject-management": Object.freeze(["class-management", "teacher-mapping", "timetable-management", "curriculum-planning"]),
  "teacher-mapping": Object.freeze(["timetable-management", "subject-management", "section-management", "leave-management"]),
  "syllabus-tracking": Object.freeze(["curriculum-planning", "lesson-plan-management", "student-performance", "academic-reports"]),
  "academic-calendar": Object.freeze(["holiday-management", "exam-scheduling", "promotion-management", "event-updates"]),
  "online-classes": Object.freeze(["study-material", "teacher-mapping", "student-app", "teacher-app"]),
  "study-material": Object.freeze(["online-classes", "curriculum-planning", "lesson-plan-management", "student-app"]),
  "student-performance": Object.freeze(["performance-insights", "attendance-analytics", "academic-reports", "student-reports"]),
  "class-management": Object.freeze(["section-management", "subject-management", "student-admission", "fee-structure"]),
  "section-management": Object.freeze(["class-management", "teacher-mapping", "student-attendance", "student-admission"]),
  "certificate-generation": Object.freeze(["document-management", "student-reports", "pending-fee-tracking", "export-pdf"]),
  "promotion-management": Object.freeze(["class-management", "section-management", "academic-calendar", "student-reports"]),

  "student-admission": Object.freeze(["enquiry-management", "class-management", "section-management", "document-management"]),
  "enquiry-management": Object.freeze(["student-admission", "front-office", "admission-reports", "website-builder-ai"]),
  "staff-management": Object.freeze(["user-management", "document-management", "staff-attendance", "payroll"]),
  "role-and-permission": Object.freeze(["user-management", "approval-workflow", "teacher-mapping", "institute-settings"]),
  "id-card-management": Object.freeze(["student-admission", "document-management", "qr-attendance", "export-pdf"]),
  "document-management": Object.freeze(["student-admission", "staff-management", "certificate-generation", "custom-forms"]),
  "transport-management": Object.freeze(["transport-fees", "transport-reports", "parent-app", "emergency-alerts"]),
  "task-management": Object.freeze(["automations", "approval-workflow", "dashboard-analytics", "teacher-app"]),
  "hostel-management": Object.freeze(["hostel-fees", "student-admission", "parent-app"]),
  "inventory-management": Object.freeze(["asset-management", "approval-workflow", "accounting-reports", "task-management"]),
  "library-management": Object.freeze(["library-reports", "student-app", "fine-management"]),
  "visitor-management": Object.freeze(["front-office", "complaint-management", "emergency-alerts"]),
  "front-office": Object.freeze(["visitor-management", "enquiry-management", "complaint-management", "task-management"]),
  "asset-management": Object.freeze(["inventory-management", "accounting-reports", "expense-management", "approval-workflow"]),
  "branch-management": Object.freeze(["branch-wise-reports", "institute-settings", "role-and-permission", "student-admission"]),
  "user-management": Object.freeze(["role-and-permission", "staff-management", "institute-settings", "teacher-app"]),
  "institute-settings": Object.freeze(["role-and-permission", "user-management", "academic-calendar", "payment-gateway"]),
  "approval-workflow": Object.freeze(["refund-management", "discount-and-scholarship", "expense-management", "inventory-management"]),
  "custom-forms": Object.freeze(["document-management", "student-admission", "student-reports"]),

  "fee-structure": Object.freeze(["category-wise-fees", "installment-setup", "fine-management", "fee-collection"]),
  "fee-collection": Object.freeze(["online-payment", "payment-gateway", "receipt-generation", "pending-fee-tracking"]),
  "online-payment": Object.freeze(["payment-gateway", "fee-collection", "automated-fee-reminders", "parent-app"]),
  "pending-fee-tracking": Object.freeze(["fee-reminder", "financial-summary", "fee-reports", "certificate-generation"]),
  "discount-and-scholarship": Object.freeze(["fee-structure", "pending-fee-tracking", "accounting-reports", "approval-workflow"]),
  "fine-management": Object.freeze(["fee-structure", "pending-fee-tracking", "approval-workflow", "accounting-reports"]),
  "receipt-generation": Object.freeze(["fee-collection", "payment-gateway", "export-pdf", "accounting-reports"]),
  "installment-setup": Object.freeze(["fee-structure", "pending-fee-tracking", "fee-collection", "parent-app"]),
  "expense-management": Object.freeze(["accounting-reports", "approval-workflow", "financial-summary", "automations"]),
  payroll: Object.freeze(["staff-management", "staff-attendance", "accounting-reports", "teacher-app"]),
  "accounting-reports": Object.freeze(["expense-management", "fee-collection", "financial-summary", "export-excel"]),
  "payment-gateway": Object.freeze(["online-payment", "fee-collection", "refund-management", "financial-summary"]),
  "refund-management": Object.freeze(["payment-gateway", "fee-collection", "approval-workflow", "accounting-reports"]),
  "fee-reminder": Object.freeze(["pending-fee-tracking", "automated-fee-reminders", "online-payment", "automations"]),
  "category-wise-fees": Object.freeze(["fee-structure", "discount-and-scholarship", "transport-fees", "hostel-fees"]),
  "transport-fees": Object.freeze(["transport-management", "fee-structure", "fee-collection", "transport-reports"]),
  "hostel-fees": Object.freeze(["hostel-management", "fee-structure", "fee-collection", "parent-app"]),
  "financial-summary": Object.freeze(["fee-reports", "accounting-reports", "fee-collection", "dashboard-analytics"]),

  "student-attendance": Object.freeze(["period-attendance", "late-entry-tracking", "attendance-alerts", "daily-attendance-report"]),
  "staff-attendance": Object.freeze(["leave-management", "staff-shift-tracking", "biometric-attendance", "staff-reports"]),
  "biometric-attendance": Object.freeze(["student-attendance", "qr-attendance", "rfid-attendance", "parent-notification"]),
  "qr-attendance": Object.freeze(["biometric-attendance", "id-card-management", "student-attendance"]),
  "rfid-attendance": Object.freeze(["biometric-attendance", "student-attendance"]),
  "leave-management": Object.freeze(["staff-attendance", "staff-shift-tracking", "teacher-mapping", "timetable-management"]),
  "holiday-management": Object.freeze(["academic-calendar", "timetable-management", "transport-management", "staff-shift-tracking"]),
  "late-entry-tracking": Object.freeze(["student-attendance", "daily-attendance-report", "monthly-attendance-report", "attendance-analytics"]),
  "daily-attendance-report": Object.freeze(["student-attendance", "attendance-analytics", "parent-notification"]),
  "monthly-attendance-report": Object.freeze(["student-attendance", "attendance-analytics", "auto-absence-alert"]),
  "parent-notification": Object.freeze(["student-attendance", "auto-absence-alert", "attendance-alerts", "whatsapp-alerts"]),
  "period-attendance": Object.freeze(["student-attendance", "timetable-management", "teacher-mapping", "monthly-attendance-report"]),
  "attendance-analytics": Object.freeze(["daily-attendance-report", "monthly-attendance-report", "performance-insights", "student-performance"]),
  "auto-absence-alert": Object.freeze(["parent-notification", "attendance-alerts", "automations", "whatsapp-alerts"]),
  "staff-shift-tracking": Object.freeze(["staff-attendance", "leave-management", "teacher-mapping", "task-management"]),

  "student-reports": Object.freeze(["class-management", "section-management", "student-performance", "export-excel"]),
  "fee-reports": Object.freeze(["pending-fee-tracking", "financial-summary", "accounting-reports", "export-excel"]),
  "attendance-reports": Object.freeze(["attendance-analytics", "daily-attendance-report", "monthly-attendance-report", "export-excel"]),
  "academic-reports": Object.freeze(["digital-gradebook", "result-publishing", "exam-reports", "export-excel"]),
  "exam-reports": Object.freeze(["exam-scheduling", "digital-gradebook", "result-publishing", "report-card-ai"]),
  "admission-reports": Object.freeze(["enquiry-management", "student-admission", "class-management", "export-excel"]),
  "staff-reports": Object.freeze(["staff-management", "staff-attendance", "payroll", "staff-shift-tracking"]),
  "transport-reports": Object.freeze(["transport-management", "transport-fees", "attendance-reports", "export-excel"]),
  "library-reports": Object.freeze(["library-management", "export-excel"]),
  "custom-reports": Object.freeze(["student-reports", "fee-reports", "export-excel", "mis-reports"]),
  "dashboard-analytics": Object.freeze(["daily-summary", "financial-summary", "performance-insights", "task-management"]),
  "performance-insights": Object.freeze(["student-performance", "attendance-analytics", "task-management", "daily-summary"]),
  "export-pdf": Object.freeze(["result-publishing", "receipt-generation", "id-card-management", "certificate-generation"]),
  "export-excel": Object.freeze(["mis-reports", "custom-reports", "student-reports", "fee-reports"]),
  "real-time-charts": Object.freeze(["dashboard-analytics", "attendance-analytics", "fee-reports", "admission-reports"]),
  "branch-wise-reports": Object.freeze(["branch-management", "financial-summary", "mis-reports"]),
  "daily-summary": Object.freeze(["dashboard-analytics", "attendance-analytics", "pending-fee-tracking", "task-management"]),
  "mis-reports": Object.freeze(["custom-reports", "student-reports", "fee-reports", "talk-to-your-data"]),

  "sms-alerts": Object.freeze(["announcements", "circular-management", "emergency-alerts", "attendance-alerts"]),
  "whatsapp-alerts": Object.freeze(["announcements", "automated-fee-reminders", "attendance-alerts", "homework-updates"]),
  "email-notifications": Object.freeze(["announcements", "circular-management", "event-updates", "emergency-alerts"]),
  "push-notifications": Object.freeze(["parent-app", "student-app", "teacher-app", "emergency-alerts"]),
  "parent-app": Object.freeze(["student-attendance", "online-payment", "result-publishing", "parent-aira"]),
  "student-app": Object.freeze(["homework-and-assignments", "student-attendance", "result-publishing", "chat-system"]),
  "teacher-app": Object.freeze(["student-attendance", "task-management", "timetable-management", "aira-assistant"]),
  "notice-board": Object.freeze(["announcements", "circular-management", "event-updates", "website-builder-ai"]),
  announcements: Object.freeze(["sms-alerts", "whatsapp-alerts", "email-notifications", "push-notifications"]),
  "event-updates": Object.freeze(["announcements", "academic-calendar", "push-notifications"]),
  "homework-updates": Object.freeze(["homework-and-assignments", "parent-app", "student-app", "whatsapp-alerts"]),
  "automated-fee-reminders": Object.freeze(["fee-reminder", "pending-fee-tracking", "whatsapp-alerts", "online-payment"]),
  "attendance-alerts": Object.freeze(["auto-absence-alert", "parent-notification", "whatsapp-alerts", "push-notifications"]),
  "complaint-management": Object.freeze(["front-office", "task-management", "emergency-alerts"]),
  "chat-system": Object.freeze(["teacher-mapping", "student-app", "teacher-app"]),
  "circular-management": Object.freeze(["announcements", "notice-board", "sms-alerts", "whatsapp-alerts"]),
  "birthday-wishes": Object.freeze(["announcements", "student-reports", "teacher-mapping"]),
  "emergency-alerts": Object.freeze(["announcements", "sms-alerts", "whatsapp-alerts", "transport-management"]),

  "aira-assistant": Object.freeze(["talk-to-your-data", "voice-aira", "automations", "task-management"]),
  "parent-aira": Object.freeze(["parent-app", "voice-aira", "attendance-alerts", "online-payment"]),
  "voice-aira": Object.freeze(["aira-assistant", "parent-aira"]),
  "migration-copilot": Object.freeze(["student-admission", "class-management", "section-management", "document-management"]),
  "compliance-ai": Object.freeze(["student-reports", "document-management", "migration-copilot", "talk-to-your-data"]),
  "report-card-ai": Object.freeze(["result-publishing", "digital-gradebook", "exam-reports"]),
  "website-builder-ai": Object.freeze(["enquiry-management", "notice-board", "announcements", "custom-forms"]),
  "talk-to-your-data": Object.freeze(["mis-reports", "custom-reports", "aira-assistant", "dashboard-analytics"]),
  automations: Object.freeze(["task-management", "fee-reminder", "auto-absence-alert", "document-management"]),
});

export const MODULE_PROFILES = Object.freeze(Object.fromEntries(
  Object.entries(MODULE_PROFILE_CONTENT).map(([key, value]) => [
    key,
    Object.freeze({ ...value, related: MODULE_RELATIONSHIPS[key] || Object.freeze([]) }),
  ]),
));

export const MODULE_PROFILE_KEYS = Object.freeze(Object.keys(MODULE_PROFILES));

/** Return a module-specific profile or null. Intentionally no family-level fallback. */
export function moduleProfileFor(moduleOrKey) {
  const key = typeof moduleOrKey === "string" ? moduleOrKey : moduleOrKey?.key;
  return key ? MODULE_PROFILES[key] || null : null;
}
